import { Chart, ChartTypes, ChartInfoIcon } from "@uicore";
import { Skeleton, Stack, Typography } from "@altimateai/lego";
import { useClusterUtilization } from "../hooks";
import { Line, ReferenceLine } from "recharts";
import { formatChartDate } from "../helpers";
import { formatPercent } from "@/helpers/utils";

interface Props {
  clusterId: string;
  startDate: string;
  endDate: string;
  aggregation?: "day" | "week" | "month";
}

const ClusterUtilizationChart = ({ clusterId, startDate, endDate, aggregation = "day" }: Props) => {
  const { data, isLoading, error } = useClusterUtilization(
    { cluster_id: clusterId, start_date: startDate, end_date: endDate, granularity: aggregation },
    Boolean(clusterId)
  );

  const chartData =
    data?.timeseries?.map(point => ({
      period: point.period,
      avg_cpu: point.avg_cpu,
      avg_memory: point.avg_memory,
    })) ?? [];

  if (isLoading) {
    return (
      <Stack direction="column" gap="md">
        <Skeleton className="al-h-6 al-w-48" />
        <Skeleton className="al-h-[300px] al-w-full" />
      </Stack>
    );
  }

  if (error || !data) {
    return <Typography className="al-text-red-500">Failed to load utilization data</Typography>;
  }

  const utilization = data.utilization;
  const avgCpu = utilization?.avg_cpu ?? 0;
  const avgMemory = utilization?.avg_memory ?? 0;

  if (chartData.length === 0) {
    return (
      <Stack align="center" justify="center" className="al-h-[300px]">
        <Typography className="al-text-gray-500">
          No utilization data available for the selected period
        </Typography>
      </Stack>
    );
  }

  return (
    <div className="al-pb-8" style={{ overflow: "visible" }}>
      <Stack direction="row" align="center" gap="md" className="al-mb-4">
        <Stack direction="row" align="center" gap="xs">
          <div className="al-h-3 al-w-3 al-rounded" style={{ backgroundColor: "#2196F3" }} />
          <Typography size="sm" className="al-text-gray-600">
            CPU: Avg {avgCpu.toFixed(1)}%
          </Typography>
        </Stack>
        <Stack direction="row" align="center" gap="xs">
          <div className="al-h-3 al-w-3 al-rounded" style={{ backgroundColor: "#9C27B0" }} />
          <Typography size="sm" className="al-text-gray-600">
            Memory: Avg {avgMemory.toFixed(1)}%
          </Typography>
        </Stack>
        <ChartInfoIcon
          title="How to read this chart"
          content="This chart shows cluster CPU and Memory utilization over time. Values below 30% (dashed line) indicate potential over-provisioning."
        />
      </Stack>
      <div style={{ width: "100%", height: 520, overflow: "visible" }}>
        <Chart
          title=""
          xAxisDataKey="period"
          yAxisLabel={{ value: "Utilization" }}
          xAxisLabelFormatter={(value: string) => formatChartDate(value, aggregation)}
          yAxisLabelFormatter={formatPercent}
          type={ChartTypes.LineChart}
          data={chartData}
          height={520}
        >
          {({ barSize: _barSize }) => (
            <>
              <ReferenceLine
                y={30}
                stroke="#FF9800"
                strokeDasharray="5 5"
                label={{ value: "Low threshold", fill: "#FF9800", fontSize: 12 }}
              />
              <Line
                name="CPU %"
                dataKey="avg_cpu"
                stroke="#2196F3"
                strokeWidth={2}
                dot={{ fill: "#2196F3", strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: "#2196F3" }}
              />
              <Line
                name="Memory %"
                dataKey="avg_memory"
                stroke="#9C27B0"
                strokeWidth={2}
                dot={{ fill: "#9C27B0", strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: "#9C27B0" }}
              />
            </>
          )}
        </Chart>
      </div>
    </div>
  );
};

export default ClusterUtilizationChart;
